import * as React from "react";
import { setRedirect } from "../../../store/contextSlice";
import { SingleNavMenu } from "../../../components/antd/layout/singleNavMenu";
import { forPage, SingleNavMenuLink, toLinkButton, toLinkDivider, toLinkSpace } from "../../identity/useNavMenu";
import { useLocation } from "react-router-dom";
import { formSet, formSetValue, useCpFormAnyValue, useCpFormValue } from "../../gvc/testNewForm/utils";
import { useAppDispatch } from "../../../store/hooks";
import { store } from "../../../store";
import { useCpFormContext } from "../../../components/form/useCpFormContext";
import { DomainInput } from "../../../schema/types/domain/input";
import { IS_DEPLOYMENT_ENV_PROD, IS_LOCAL } from "../../../envVariables";
import { getDefaultDomainPortInput, getDefaultDomainPortRouteInput } from "./utils/utils";

interface Props {
  submit: () => Promise<void>;
  ignoreBlockerRef: React.RefObject<boolean>;
}
export const DomainDraftNav: React.FC<Props> = ({ submit, ignoreBlockerRef }) => {
  const { id } = useCpFormContext();
  const dispatch = useAppDispatch();

  const { pathname } = useLocation();

  const submitting = useCpFormAnyValue(`meta.submitting`);
  const draftType = useCpFormValue(`meta.draftType`);
  const mode = useCpFormValue(`meta.mode`);
  const routingMode = useCpFormValue(`advanced.spec.routingMode`);

  const links = React.useMemo(() => {
    const { toLinkSection } = forPage("/content");

    const links: SingleNavMenuLink[] = [];

    if (mode === "basic") {
      links.push(toLinkSection("Domain", "general", { names: [`root`, `basic.name`, `basic.description`, `basic.workload`] }));
      links.push(toLinkSection("Tags", "tags", { names: [`tags`] }));
      links.push(toLinkSpace());
      links.push(
        toLinkButton("Cancel", {
          type: "primary",
          onClick: () => {
            ignoreBlockerRef.current = true;
            if (draftType === "edit") {
              const form = store.getState().form.instances[id];
              const values: DomainInput = form.values as any;
              const domainLink = values.meta.domainLink;

              dispatch(setRedirect(`/console${domainLink}`));
              return;
            }
            const back = pathname.split(`/-draft`)[0];
            dispatch(setRedirect(back));
          },
          danger: true,
        }),
        toLinkButton("Advanced", {
          type: "default",
          onClick: () => {
            const form = store.getState().form.instances[id];
            const values: DomainInput = form.values;
            if (values.basic.name) {
              formSetValue(id, `advanced.name`, values.basic.name);
              if (values.basic.workload.value) {
                const port = getDefaultDomainPortInput();
                port.routes = [
                  {
                    ...getDefaultDomainPortRouteInput(),
                    prefix: `/`,
                    workload: values.basic.workload,
                  },
                ];
                formSetValue(id, `meta.portActiveKey`, port.id);
                formSetValue(id, `advanced.spec.ports`, [port]);
              }
            }

            formSetValue(id, `meta.mode`, "advanced");

            const onBlurCounter = form.meta.onBlurCounter;
            formSet(id, "meta.onBlurCounter", onBlurCounter + 1);
          },
        }),
        toLinkButton("Preview", {
          type: "default",
          onClick: () => formSet(id, `meta.previewing`, true),
          disabled: submitting,
        }),
        toLinkButton(draftType === "edit" ? "Update" : "Create", {
          type: "primary",
          onClick: async () => {
            await submit();
          },
          loading: submitting,
          disabled: submitting,
          className: "action",
        }),
      );

      if (IS_LOCAL || !IS_DEPLOYMENT_ENV_PROD) {
        links.push(
          toLinkButton("Debug", {
            type: "default",
            onClick: () => formSet(id, `meta.debugging`, true),
          }),
        );
      }
    } else {
      links.push(
        toLinkSection("Domain", "name", { names: [`root`, `advanced.name`] }),
        toLinkSection("Options", "general", {
          names: [
            `advanced.spec.dnsMode`,
            `advanced.spec.certChallengeType`,
            `advanced.spec.routingMode`,
            `advanced.spec.acceptAll`,
          ],
        }),
      );

      if (routingMode === "workloadSubdomain") {
        links.push(toLinkSection("Workload", "workload", { names: [`advanced.spec.workload`] }));
      } else if (routingMode !== "none") {
        links.push(toLinkSection("GVC", "gvc", { names: [`gvc`] }));
      }

      links.push(
        toLinkSection("Ports", "ports", { names: [`advanced.spec.ports`] }),
        toLinkDivider(),
        toLinkSection("Tags", "tags", { names: [`tags`] }),
        toLinkSpace(),
        toLinkButton("Cancel", {
          type: "primary",
          onClick: () => {
            ignoreBlockerRef.current = true;
            if (draftType === "edit") {
              const form = store.getState().form.instances[id];
              const values: DomainInput = form.values as any;
              const domainLink = values.meta.domainLink;

              dispatch(setRedirect(`/console${domainLink}`));
              return;
            }
            const back = pathname.split(`/-draft`)[0];
            dispatch(setRedirect(back));
          },
          disabled: submitting,
          danger: true,
        }),
        toLinkButton("Preview", {
          type: "default",
          onClick: () => formSet(id, `meta.previewing`, true),
          disabled: submitting,
        }),
        toLinkButton(draftType === "edit" ? "Update" : "Create", {
          type: "primary",
          onClick: async () => {
            await submit();
          },
          loading: submitting,
          disabled: submitting,
          className: "action",
        }),
      );

      if (IS_LOCAL || !IS_DEPLOYMENT_ENV_PROD) {
        links.push(
          toLinkButton("Debug", {
            type: "default",
            onClick: () => formSet(id, `meta.debugging`, true),
          }),
        );
      }
    }

    return links;
  }, [mode, draftType, routingMode, submitting]);

  return <SingleNavMenu useDetailHeader={false} links={links} pagesWithScrolls={[`content`]} />;
};
